import axios from 'axios';
import toast from 'react-hot-toast';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '/api',
  timeout: 15000,
});

// Request interceptor - attach token
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
}, (error) => Promise.reject(error));

// Response interceptor - handle errors globally
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const message = error.response?.data?.message || 'Something went wrong';
    if (error.response?.status === 401) {
      // Only redirect if we have a stored token (expired session), not on login attempts
      const hasToken = localStorage.getItem('token');
      if (hasToken) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        // Only redirect if not already on login page to avoid refresh loop
        if (!window.location.pathname.startsWith('/login')) {
          // Keep the "Connect with AINA Suite" request so login can return to it
          const next = window.location.pathname.startsWith('/connect')
            ? `?next=${encodeURIComponent(window.location.pathname + window.location.search)}` : '';
          window.location.href = `/login${next}`;
        }
      } else {
        toast.error(message);
      }
    } else if (error.response?.status !== 404) {
      toast.error(message);
    }
    return Promise.reject(error);
  }
);

// Auth
export const authAPI = {
  login: (data) => api.post('/auth/login', data),
  register: (data) => api.post('/auth/register', data),
  getMe: () => api.get('/auth/me'),
  logout: () => api.post('/auth/logout'),
  changePassword: (data) => api.patch('/auth/change-password', data),
  forgotPassword: (data) => api.post('/auth/forgot-password', data),
  resetPassword: (token, data) => api.post(`/auth/reset-password/${token}`, data),
};

// Admin
export const adminAPI = {
  getDashboard: () => api.get('/admin/dashboard'),
  getMerchants: (params) => api.get('/admin/merchants', { params }),
  createMerchant: (data) => api.post('/admin/merchants', data),
  getMerchant: (id) => api.get(`/admin/merchants/${id}`),
  updateMerchantStatus: (id, data) => api.patch(`/admin/merchants/${id}/status`, data),
  updateMerchantPlan: (id, data) => api.patch(`/admin/merchants/${id}/plan`, data),
  updateMerchantPassword: (id, data) => api.patch(`/admin/merchants/${id}/password`, data),
  updateMerchantEmail: (id, data) => api.patch(`/admin/merchants/${id}/email`, data),
  updateMerchantBusinessType: (id, data) => api.patch(`/admin/merchants/${id}/business-type`, data),
  createInvoice: (id, data) => api.post(`/admin/merchants/${id}/invoice`, data),
  getInvoices: (params) => api.get('/admin/invoices', { params }),
  getActivity: () => api.get('/admin/activity'),
  getInvoiceSettings: () => api.get('/admin/invoice-settings'),
  updateInvoiceSettings: (data) => api.put('/admin/invoice-settings', data),
  createCustomInvoice: (data) => api.post('/admin/invoices', data),
  downloadInvoicePDF: (id) => api.get(`/admin/invoices/${id}/pdf`, { responseType: 'blob' }),
  updateInvoiceStatus: (id, data) => api.patch(`/admin/invoices/${id}/status`, data),
  updateMerchantMediaTypes: (id, data) => api.patch(`/admin/merchants/${id}/media-types`, data),
  updateMerchantFeatures: (id, data) => api.patch(`/admin/merchants/${id}/features`, data),
  getPlans: () => api.get('/admin/plans'),
  createPlan: (data) => api.post('/admin/plans', data),
  updatePlan: (id, data) => api.patch(`/admin/plans/${id}`, data),
  deletePlan: (id) => api.delete(`/admin/plans/${id}`),
};

// Merchant
export const merchantAPI = {
  getMe: () => api.get('/merchants/me'),
  update: (data) => api.patch('/merchants/me', data),
  updateAiConfig: (data) => api.patch('/merchants/me/ai-config', data),
  updateNotifications: (data) => api.patch('/merchants/me/notifications', data),
  updateHandoffConfig: (data) => api.patch('/merchants/me/handoff-config', data),
  updateOrderConfig: (data) => api.patch('/merchants/me/order-config', data),
  updateBusinessType: (data) => api.patch('/merchants/me/business-type', data),
  updateDataSheet: (data) => api.patch('/merchants/me/data-sheet', data),
  getUsage: () => api.get('/merchants/me/usage'),
  getPrompts: () => api.get('/merchants/me/prompts'),
  createPrompt: (data) => api.post('/merchants/me/prompts', data),
  updatePrompt: (id, data) => api.patch(`/merchants/me/prompts/${id}`, data),
  deletePrompt: (id) => api.delete(`/merchants/me/prompts/${id}`),
  assignChannelPrompt: (channelId, data) => api.patch(`/merchants/${channelId}/prompt`, data),
  getAgents: () => api.get('/merchants/me/agents'),
  inviteAgent: (data) => api.post('/merchants/me/agents', data),
  updateAgent: (id, data) => api.patch(`/merchants/me/agents/${id}`, data),
  resetAgentPassword: (id, data) => api.patch(`/merchants/me/agents/${id}/password`, data),
  removeAgent: (id) => api.delete(`/merchants/me/agents/${id}`),
  getMediaTypes: () => api.get('/merchants/me/media-types'),
  updateMediaTypes: (data) => api.patch('/merchants/me/media-types', data),
  getNamedSheets: () => api.get('/merchants/me/named-sheets'),
  addNamedSheet: (data) => api.post('/merchants/me/named-sheets', data),
  updateNamedSheet: (id, data) => api.patch(`/merchants/me/named-sheets/${id}`, data),
  deleteNamedSheet: (id) => api.delete(`/merchants/me/named-sheets/${id}`),
};

// Customers
export const customersAPI = {
  list: (params) => api.get('/customers', { params }),
  get: (id) => api.get(`/customers/${id}`),
  update: (id, data) => api.patch(`/customers/${id}`, data),
  block: (id, data) => api.patch(`/customers/${id}/block`, data),
  setApproval: (id, status) => api.patch(`/customers/${id}/approval`, { status }),
  getTags: () => api.get('/customers/tags/all'),
};

// Messages
export const messagesAPI = {
  getInbox: (params) => api.get('/messages/inbox', { params }),
  getConversation: (customerId, params) => api.get(`/messages/conversation/${customerId}`, { params }),
  send: (data) => api.post('/messages/send', data),
  sendMedia: (formData) => api.post('/messages/send-media', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  endChat: (customerId) => api.post(`/messages/end-chat/${customerId}`),
  getStats: () => api.get('/messages/stats'),
};

// Channels
export const channelsAPI = {
  list: () => api.get('/channels'),
  create: (data) => api.post('/channels', data),
  get: (id) => api.get(`/channels/${id}`),
  update: (id, data) => api.patch(`/channels/${id}`, data),
  delete: (id) => api.delete(`/channels/${id}`),
  connect: (id, data) => api.post(`/channels/${id}/connect`, data),
  disconnect: (id) => api.post(`/channels/${id}/disconnect`),
  getQR: (id) => api.get(`/channels/${id}/qr`),
};

// Connect with AINA Suite (partner apps)
export const connectAPI = {
  // Merchant
  authorize: (params) => api.get('/connect/authorize', { params }),
  approve: (data) => api.post('/connect/approve', data),
  listApps: () => api.get('/connect/apps'),
  revokeApp: (channelId) => api.delete(`/connect/apps/${channelId}`),
  // Super admin
  listClients: () => api.get('/connect/clients'),
  createClient: (data) => api.post('/connect/clients', data),
  updateClient: (id, data) => api.patch(`/connect/clients/${id}`, data),
  rotateClientSecret: (id) => api.post(`/connect/clients/${id}/rotate-secret`),
  deleteClient: (id) => api.delete(`/connect/clients/${id}`),
};

// Integrations
export const integrationsAPI = {
  getGoogleStatus: () => api.get('/integrations/google/status'),
  getGoogleAuthUrl: () => api.get('/integrations/google/auth-url'),
  disconnectGoogle: () => api.delete('/integrations/google'),
  listSpreadsheets: () => api.get('/integrations/google/spreadsheets'),
  listSheetTabs: (id) => api.get(`/integrations/google/spreadsheets/${id}/tabs`),
  previewSheet: (params) => api.get('/integrations/google/preview', { params }),
  connectSheet: (data) => api.post('/integrations/google/sheets', data),
  getConnectedSheets: () => api.get('/integrations/google/sheets'),
  disconnectSheet: (id) => api.delete(`/integrations/google/sheets/${id}`),
  syncSheet: (id) => api.post(`/integrations/google/sheets/${id}/sync`),
  updateSheetInstructions: (id, aiInstructions) => api.patch(`/integrations/google/sheets/${id}/instructions`, { aiInstructions }),
  // Files
  uploadFile: (formData) => api.post('/integrations/files/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 60000 }),
  getFiles: () => api.get('/integrations/files'),
  updateFile: (id, data) => api.patch(`/integrations/files/${id}`, data),
  deleteFile: (id) => api.delete(`/integrations/files/${id}`),
  downloadFile: (id) => api.get(`/integrations/files/${id}/download`, { responseType: 'blob' }),
  // Calendar
  getCalendarStatus: () => api.get('/integrations/calendar/status'),
  connectCalendar: (data) => api.post('/integrations/calendar/connect', data),
  disconnectCalendar: () => api.delete('/integrations/calendar'),
  // Drive
  getPickerToken: () => api.get('/integrations/drive/picker-token'),
  getDriveFiles: (params) => api.get('/integrations/drive/files', { params }),
  importDriveFile: (fileId, data) => api.post(`/integrations/drive/import/${fileId}`, data),
  // Databases
  getDatabases: () => api.get('/integrations/databases'),
  connectDatabase: (data) => api.post('/integrations/databases', data),
  testDatabase: (data) => api.post('/integrations/databases/test', data),
  disconnectDatabase: (id) => api.delete(`/integrations/databases/${id}`),
  updateDatabase: (id, data) => api.patch(`/integrations/databases/${id}`, data),
  getDatabaseTables: (id) => api.get(`/integrations/databases/${id}/tables`),
  updateDatabaseInstructions: (id, aiInstructions) => api.patch(`/integrations/databases/${id}/instructions`, { aiInstructions }),
};

// Orders
export const ordersAPI = {
  list: (params) => api.get('/orders', { params }),
  updateStatus: (id, status) => api.patch(`/orders/${id}/status`, { status }),
};

// Appointments
export const appointmentsAPI = {
  list: (params) => api.get('/appointments', { params }),
  updateStatus: (id, status) => api.patch(`/appointments/${id}/status`, { status }),
};

// Leads
export const leadsAPI = {
  list: (params) => api.get('/leads', { params }),
  updateStatus: (id, status) => api.patch(`/leads/${id}/status`, { status }),
  fromCustomer: (data) => api.post('/leads/from-customer', data),
};

// Tickets
export const ticketsAPI = {
  list: (params) => api.get('/tickets', { params }),
  updateStatus: (id, status) => api.patch(`/tickets/${id}/status`, { status }),
};

// CRM
export const crmAPI = {
  getPipeline: () => api.get('/crm/pipeline'),
  getStats: () => api.get('/crm/stats'),
  getDeals: (params) => api.get('/crm/deals', { params }),
  getDeal: (id) => api.get(`/crm/deals/${id}`),
  createDeal: (data) => api.post('/crm/deals', data),
  updateDeal: (id, data) => api.patch(`/crm/deals/${id}`, data),
  moveStage: (id, stage, lostReason) => api.patch(`/crm/deals/${id}/stage`, { stage, lostReason }),
  addNote: (id, text) => api.post(`/crm/deals/${id}/notes`, { text }),
  addActivity: (id, data) => api.post(`/crm/deals/${id}/activities`, data),
  deleteDeal: (id) => api.delete(`/crm/deals/${id}`),
};

// Billing
export const billingAPI = {
  getPlans: () => api.get('/billing/plans'),
  getInvoices: (params) => api.get('/billing/invoices', { params }),
  downloadInvoicePDF: (id) => api.get(`/billing/invoices/${id}/pdf`, { responseType: 'blob' }),
};

// Push Notifications
export const pushAPI = {
  getVapidKey: () => api.get('/push/vapid-key'),
  subscribe: (subscription) => api.post('/push/subscribe', { subscription }),
  unsubscribe: (endpoint) => api.delete('/push/unsubscribe', { data: { endpoint } }),
};

// Public Booking
export const bookingAPI = {
  getConfig: (merchantId) => api.get(`/booking/${merchantId}/config`),
  getSlots: (merchantId, params) => api.get(`/booking/${merchantId}/slots`, { params }),
  book: (merchantId, data) => api.post(`/booking/${merchantId}/book`, data),
  sendOtp: (merchantId, data) => api.post(`/booking/${merchantId}/send-otp`, data),
  verifyOtp: (merchantId, data) => api.post(`/booking/${merchantId}/verify-otp`, data),
  cancel: (merchantId, appointmentId, data) => api.post(`/booking/${merchantId}/cancel/${appointmentId}`, data),
};

// Workflows
export const workflowsAPI = {
  list: () => api.get('/workflows'),
  get: (id) => api.get(`/workflows/${id}`),
  create: (data) => api.post('/workflows', data),
  update: (id, data) => api.patch(`/workflows/${id}`, data),
  delete: (id) => api.delete(`/workflows/${id}`),
  assignChannel: (id, channelId) => api.post(`/workflows/${id}/channels`, { channelId }),
  removeChannel: (id, channelId) => api.delete(`/workflows/${id}/channels/${channelId}`),
};

// Extraction Templates (Telegram image → Google Sheet)
export const extractionAPI = {
  list: () => api.get('/extraction-templates'),
  create: (data) => api.post('/extraction-templates', data),
  update: (id, data) => api.patch(`/extraction-templates/${id}`, data),
  delete: (id) => api.delete(`/extraction-templates/${id}`),
};

// Analytics
export const analyticsAPI = {
  getOverview: (params) => api.get('/analytics/overview', { params }),
  getChannels: () => api.get('/analytics/channels'),
};

export default api;
